import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { useSeo } from '@/hooks/useSeo';
import { useScrollAnimation } from '@/hooks/useAnimations';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const SUBS = ['Collane', 'Bracciali', 'Anelli', 'Orecchini'];

export default function ArgentoPage() {
  useSeo({
    title: 'Collezione Amira in Argento — Collane e Bracciali | Ciro Gallotti',
    description:
      'La Collezione Argento di Ciro Gallotti: collane e bracciali in argento 925, lavorati a mano nei laboratori orafi di Napoli. Eleganza contemporanea, luce pura.',
    path: '/argento',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://cirogallotti.com/' },
        { '@type': 'ListItem', position: 2, name: 'Argento', item: 'https://cirogallotti.com/argento' },
      ],
    },
  });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const subParam = searchParams.get('sub');
  const activeSub = SUBS.includes(subParam) ? subParam : 'Collane';

  const grid = useScrollAnimation(0.1);

  useEffect(() => {
    axios
      .get(`${API}/products?collection=Argento`)
      .then((res) => setProducts(res.data || []))
      .catch((e) => console.error('Errore caricamento collezione argento:', e))
      .finally(() => setLoading(false));
  }, []);

  const setSub = (sub) => {
    const next = new URLSearchParams(searchParams);
    next.set('sub', sub);
    setSearchParams(next);
  };

  const counts = Object.fromEntries(SUBS.map((s) => [s, products.filter((p) => p.category === s).length]));
  const visible = products
    .filter((p) => p.category === activeSub)
    .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <div className="bg-ivory pt-20 md:pt-22" data-testid="argento-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-secondary/40" data-testid="argento-hero">
        <h1 className="sr-only">Collezione Amira — Argento 925</h1>
        <img
          src="/amira-hero.jpg"
          alt="Collezione Amira — Eleganza senza tempo. Collana tennis in argento, Ciro Gallotti"
          className="w-full h-auto block"
          fetchpriority="high"
          data-testid="argento-hero-image"
        />
        <div className="relative max-w-container mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-12">
          <p className="max-w-2xl text-ink/70 leading-relaxed">
            Collane e bracciali in argento puro, lavorati a mano nei nostri laboratori orafi di Napoli.
            Linee essenziali e luce fredda per una collezione dall’eleganza contemporanea.
          </p>
        </div>
      </section>

      {/* Sub-collezioni */}
      <div className="max-w-container mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="argento-tabs">
          {SUBS.map((sub) => (
            <button
              key={sub}
              onClick={() => setSub(sub)}
              className={`px-6 py-2.5 rounded-full text-eyebrow border transition-colors duration-hover ${
                activeSub === sub
                  ? 'border-bordeaux bg-bordeaux text-ivory'
                  : 'border-line text-ink/70 hover:border-ink'
              }`}
              data-testid={`argento-tab-${sub}`}
            >
              {sub}
              <span className={`ml-2 ${activeSub === sub ? 'text-ivory/70' : 'text-muted'}`}>
                {counts[sub]}
              </span>
            </button>
          ))}
        </div>

        <div ref={grid.ref}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-line rounded-md mb-5" />
                  <div className="h-3 bg-line w-20 mb-3" />
                  <div className="h-5 bg-line w-40 mb-2" />
                  <div className="h-4 bg-line w-24" />
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-24 border border-line rounded-2xl" data-testid="argento-empty">
              <p className="font-display text-h3 text-ink mb-3">Presto disponibili</p>
              <p className="text-muted mb-8 max-w-md mx-auto">
                {activeSub} in argento: i nuovi pezzi stanno per arrivare. Nel frattempo, scopri il
                resto della collezione o prenota un appuntamento in showroom.
              </p>
              <Button variant="accent" onClick={() => (window.location.href = '/collezione')}>
                Vai alla Collezione
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-12">
              {visible.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
